import React, { useEffect } from 'react';
import ModalBasic from '../../components/infrastructure/Modals/ModalBasic';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { object, string } from 'yup';
import FormikInputGroup from '../../components/formik/FormikInputGroup';
import FormikSelectGroup from '../../components/formik/FormikSelectGroup';
import PrimaryButton from '../../components/infrastructure/Buttons/PrimaryButton';
import FormikTextareaGroup from '../../components/formik/FormikTextareaGroup';
import { useDispatch, useSelector } from 'react-redux';
import {
    editComplaint,
    fetchComplaints,
} from '../../app/reducers/Complaints/complaintSlice';
import FormikDirectFileUpload from '../../components/formik/FormikDirectFileUpload';
import FormikDirectImageUpload from '../../components/formik/FormikDirectImageUpload';
import DangerButton from '../../components/infrastructure/Buttons/DangerButton';
import { MACHINE_WARRANTY_OPTIONS } from '../../utils/dropdownOptions';
import FormikMultiSelect from '../../components/formik/FormikMultiSelect';
import { fetchEmployees, getEmployees } from '../../app/reducers/Users/EmployeeSlice';
import { generateEmployeeOptions } from '../../utils/Utils';
import { getAuth } from '../../app/reducers/Auth/authSlice';

const CloseComplaintModal = ({
    isCloseComplaintBtnOpen,
    setIsCloseComplaintBtnOpen,
    closeComplaintData,
    page,
    status
}) => {
    const dispatch = useDispatch();
    const { employees, loading } = useSelector(getEmployees); // that redux call from it parent 
    const resolutionFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            ...closeComplaintData,
            resolution: '',
            resolutionPhotos: closeComplaintData?.resolutionPhotos
                ? closeComplaintData.resolutionPhotos
                : [],
            resolutionVideos: closeComplaintData?.resolutionVideos
                ? closeComplaintData.resolutionVideos
                : [],
            machineNo: closeComplaintData?.machineNo || '',
            team: closeComplaintData?.team || '',
            machineWarranty: closeComplaintData?.machineWarranty || '',
            isOtherMachineWarranty: false,
            isOtherTeam: false,
        },
        validationSchema: object({
            machineNo: string().required('Machine number is required'),
            machineWarranty: string().required('Machine warranty is required'),
        }),
        onSubmit: async (values) => {
            console.log(values);
            await dispatch(editComplaint({ ...values, status: 'closed' }));
            await dispatch(
                fetchComplaints({
                    populate: true,
                    page,
                    status,
                })
            );
            setIsCloseComplaintBtnOpen(false);
            resolutionFormik.resetForm();
        },
    });


    const generateCustomEmployeeOptions = (employeeDocs) => {
        return (employeeDocs ?? []).map((emp) => {
            const fullName = `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim();

            return {
                label: fullName,
                value: fullName,
            };
        });
    };


    return (
        <ModalBasic
            title={`Add Resolution`}
            setModalOpen={setIsCloseComplaintBtnOpen}
            modalOpen={isCloseComplaintBtnOpen}
            max_width="max-w-lg"
        >
            <form
                onSubmit={resolutionFormik.handleSubmit}
                className="flex flex-col gap-3 p-3"
            >
                <div className="grid grid-cols-2 gap-3 pb-4 border-b border-slate-200">
                    <FormikInputGroup formik={resolutionFormik} label="Complaint No." name="complaintNumber" readOnly value={`CMP-${resolutionFormik.values.complaintNumber || ''}`} />
                    <FormikInputGroup formik={resolutionFormik} label="Customer" name="customer.name" readOnly />
                    <FormikInputGroup formik={resolutionFormik} label="Company" name="customer.companyData.name" readOnly />
                    <FormikInputGroup formik={resolutionFormik} label="Machine" name="product.name" readOnly />
                    <FormikInputGroup formik={resolutionFormik} label="Technician" name="assignedTo" readOnly />
                    <div className="col-span-2">
                        <FormikInputGroup formik={resolutionFormik} label="Description" name="description" readOnly fullWidth />
                    </div>
                </div>

                <FormikInputGroup
                    formik={resolutionFormik}
                    label="Machine No"
                    name="machineNo"
                    required
                />

                {resolutionFormik.values.isOtherTeam ? (
                    <div>
                        <FormikInputGroup
                            name="team"
                            label="Team"
                            formik={resolutionFormik}
                            required
                        />
                        <p
                            className="text-blue-600 text-sm cursor-pointer underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                resolutionFormik.setFieldValue('team', '');
                                resolutionFormik.setFieldValue(
                                    'isOtherTeam',
                                    false
                                );
                            }}
                        >
                            Select from list
                        </p>
                    </div>
                ) : (
                    < FormikSelectGroup
                        formik={resolutionFormik}
                        label="Team"
                        name="team"
                        options={[...generateCustomEmployeeOptions(employees?.docs), { label: 'other', value: 'other' }]}
                        onChange={(selectedOption) => {
                            console.log("selectedOption", selectedOption)
                            if (selectedOption?.value === 'other') {
                                resolutionFormik.setFieldValue('team', '');
                                resolutionFormik.setFieldValue(
                                    'isOtherTeam',
                                    true
                                );
                            } else {
                                resolutionFormik.setFieldValue(
                                    'team',
                                    selectedOption?.value
                                );
                                resolutionFormik.setFieldValue(
                                    'isOtherTeam',
                                    false
                                );
                            }
                        }}
                    />)}

                {resolutionFormik.values.isOtherMachineWarranty ? (
                    <div>
                        <FormikInputGroup
                            name="machineWarranty"
                            label="Machine Warranty"
                            formik={resolutionFormik}
                            required
                        />
                        <p
                            className="text-blue-600 text-sm cursor-pointer underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                resolutionFormik.setFieldValue('machineWarranty', '');
                                resolutionFormik.setFieldValue(
                                    'isOtherMachineWarranty',
                                    false
                                );
                            }}
                        >
                            Select from list
                        </p>
                    </div>
                ) : (
                    <FormikSelectGroup
                        label="Machine Warranty"
                        name="machineWarranty"
                        formik={resolutionFormik}
                        options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
                        { label: 'AMC', value: 'amc' },
                        { label: 'Other', value: 'other' },
                        ]}
                        required
                        onChange={(selectedOption) => {
                            if (selectedOption?.value === 'other') {
                                resolutionFormik.setFieldValue('machineWarranty', '');
                                resolutionFormik.setFieldValue(
                                    'isOtherMachineWarranty',
                                    true
                                );
                            } else {
                                resolutionFormik.setFieldValue(
                                    'machineWarranty',
                                    selectedOption?.value
                                );
                                resolutionFormik.setFieldValue(
                                    'isOtherMachineWarranty',
                                    false
                                );
                            }
                        }}
                    />
                )}

                <FormikTextareaGroup
                    name="resolution"
                    formik={resolutionFormik}
                    label="Resolution"
                />
                <p>Photos</p>
                {resolutionFormik?.values?.product && (
                    <FormikProvider value={resolutionFormik}>
                        <FieldArray
                            name="resolutionPhotos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col">
                                        {resolutionFormik?.values?.resolutionPhotos?.map(
                                            (ele, i) => (
                                                <div className="flex items-center gap-2">
                                                    <FormikDirectImageUpload
                                                        label={`photo ${i + 1}`}
                                                        formik={
                                                            resolutionFormik
                                                        }
                                                        name={`resolutionPhotos.${i}`}
                                                        randomize
                                                        location={
                                                            'iml_group/complaints/'
                                                        }
                                                    />
                                                  
                                                    <div>
                                                        <DangerButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                arrayHelpers.remove(
                                                                    i
                                                                );
                                                            }}
                                                        >
                                                            Remove
                                                        </DangerButton>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                        <div>
                                            <PrimaryButton
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Photo
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                )}
                <p>Videos</p>

                {resolutionFormik?.values?.product && (
                    <FormikProvider value={resolutionFormik}>
                        <FieldArray
                            name="resolutionVideos"
                            render={(arrayHelpers) => {
                                return (
                                    <div className="flex flex-col">
                                        {resolutionFormik?.values?.resolutionVideos?.map(
                                            (ele, i) => (
                                                <div className="flex items-center gap-2">
                                                    <FormikDirectFileUpload
                                                        label={`video ${i + 1}`}
                                                        formik={
                                                            resolutionFormik
                                                        }
                                                        name={`resolutionVideos.${i}`}
                                                        randomize
                                                        location={
                                                            'iml_group/complaints/'
                                                        }
                                                    />
                                                    <div>
                                                        <DangerButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                arrayHelpers.remove(
                                                                    i
                                                                );
                                                            }}
                                                        >
                                                            Remove
                                                        </DangerButton>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                        <div>
                                            <PrimaryButton
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    arrayHelpers.push('');
                                                }}
                                            >
                                                Add Video
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </FormikProvider>
                )}
                <div>
                    <PrimaryButton type="submit">Submit</PrimaryButton>
                </div>
            </form>
        </ModalBasic>
    );
};

export default CloseComplaintModal;
